import { HttpProgressEvent } from '@angular/common/http';

import { HttpStatusCode } from '../shared/constants/http-status-code.enum';
import { GraphDriveItem, GraphUploadSession, GraphUploadSessionStatus } from '../shared/models/onedrive-graph.model';
import { Range } from '../shared/models/range.model';
import { OneDriveService } from '../shared/services/onedrive.service';
import { WindowService } from '../shared/services/window.service';
import { ActivityBandwidthVerifier } from '../shared/utilities/activity-bandwidth-verifier.utility';
import { GraphErrorResolver } from '../shared/utilities/graph-error-resolver.utility';
import { PromiseCancellationToken } from '../shared/utilities/promise-cancellation-token.utility';
import { ResumableUploadState, UploadSessionBase } from './models/upload-session.model';
import { UploadConfig, UploadConfigService } from './upload-config.service';

// This file was adapted from the OneDrive codebase: https://onedrive.visualstudio.com/OneDriveWeb/_git/odsp-next?path=%2Fsrc%2Flocal%2FdataSources%2Fupload%2Fvroom%2FVroomUploader.ts&version=GBmaster&_a=contents

/**
 * Service acting as a state machine that uploads documents to OneDrive.
 */
export class UploadStateMachine {

    private readonly uploadConfig: UploadConfig;

    constructor(
        private readonly oneDriveService: OneDriveService,
        uploadConfigService: UploadConfigService,
        private readonly windowService: WindowService
    ) {
        this.uploadConfig = uploadConfigService.config;
    }

    /**
    * Performs the required action based on upload state and outputs the next state.
    */
    public advanceState(session: UploadSessionBase, state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        const {
            sessionUrl,
            nextExpectedRanges
        } = state;

        let getNextState: Promise<ResumableUploadState>;

        if (!sessionUrl) {
            getNextState = this.createSessionState(session, state, cancellationToken);
        } else if (!nextExpectedRanges) {
            getNextState = this.getStatusState(session, state, cancellationToken);
        } else {
            getNextState = this.uploadItemState(session, state, cancellationToken);
        }

        return getNextState.then((nextState: ResumableUploadState) => {
            if (nextState.error) {
                // Determine whether or not recovery is possible and update the state accordingly.
                return this.handleGeneralErrorState(nextState, cancellationToken);
            } else {
                // The step was successful, so reset the retry behavior.
                return this.getState(nextState, {
                    waitDuration: undefined,
                    clientAttemptsRemaining: undefined,
                    serverAttemptsRemaining: undefined
                });
            }
        });
    }

    /**
    * Creates an upload session and updates state.
    */
    private createSessionState(session: UploadSessionBase, state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        return this.oneDriveService.createUploadSession(
            session.file,
            session.sessionId,
            null,
            null,
            cancellationToken
        ).then((response: GraphUploadSession) =>
            this.getState(state, {
                    nextExpectedRanges: response.nextExpectedRanges,
                    sessionUrl: response.uploadUrl
                }),
            error => this.getState(state, { // TODO: Should we allow the user to customize the conflict behavior?
                        error
                    }));
    }

    private getStatusState(session: UploadSessionBase, state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        return this.oneDriveService.getUploadSessionStatus(state.sessionUrl, session.sessionId, cancellationToken).then(
            (response: GraphUploadSessionStatus) =>
                this.getState(state,
                    {
                        nextExpectedRanges: response.nextExpectedRanges
                    }),
            error =>
                this.getState(state,
                    {
                        error
                    }));
    }

    /**
    * Performs a fragment upload or cancels the session and updates state.
    */
    private uploadItemState(session: UploadSessionBase, state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        const fileSize = session.file.size;

        const nextRange = this.getNextRange(fileSize, state.nextExpectedRanges);

        let pendingNextState: Promise<ResumableUploadState>;

        // TODO: Allow commits of finished uploads once the api onboards to graph
        if (nextRange && nextRange.start < fileSize) {
            pendingNextState = this.uploadFragmentState(nextRange, session, state, cancellationToken);
        } else {
            // There is nothing left to upload, but it is not possible to commit.
            // In this case, clear the session and restart since this is the only way to finish the upload.
            pendingNextState = this.cancelSessionState(session, state, cancellationToken);
        }

        return pendingNextState.then((nextState: ResumableUploadState) => {
            if (nextState.error) {
                return this.handleUploadItemErrorState(nextState, cancellationToken);
            }

            return nextState;
        });
    }

    /**
    * Uploads a fragment and updates state.
    */
    private uploadFragmentState(nextRange: Range,
        session: UploadSessionBase,
        state: ResumableUploadState,
        manualcancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        const {
            sessionUrl,
            speed = this.uploadConfig.initialSpeed
        } = state;

        const {
            start: startByte,
            end: suggestedEndByte
        } = nextRange;

        session.updateProgress(startByte);

        const fragmentSize = this.getFragmentSize(speed);

        const endByte = Math.min(startByte + fragmentSize, suggestedEndByte);

        const timeout = speed > this.uploadConfig.minSpeed
            ? this.uploadConfig.fragmentTimeoutInMs
            : (this.uploadConfig.fragmentTimeoutInMs * this.uploadConfig.slowFragmentTimeoutMultiplier);

        const bandwidthVerifier = new ActivityBandwidthVerifier(timeout, endByte - startByte);
        const file = session.file;

        const fragmentCancellationToken = new PromiseCancellationToken();

        manualcancellationToken.onCancel(() => {
            fragmentCancellationToken.cancel();
        });

        return new Promise<ResumableUploadState>(resolve => {
            this.oneDriveService.uploadFragment(
                sessionUrl,
                startByte,
                endByte,
                file.size,
                file.slice(startByte, endByte),
                session.sessionId,
                (progressEvent: HttpProgressEvent) => {
                    const lastByte = startByte + progressEvent.loaded;

                    if (speed > this.uploadConfig.minSpeed &&
                        !bandwidthVerifier.doesActivityHaveRemainingCapacity(lastByte - startByte)) {
                        // If it is not possible to upload the remaining bytes in time,
                        // give up now and lower the fragment size for the next attempt.
                        fragmentCancellationToken.cancel();
                        resolve(this.getState(state,
                            {
                                speed: Math.max(speed * this.uploadConfig.slowSpeedDowngradeRatio,
                                    this.uploadConfig.minSpeed),
                                nextExpectedRanges: undefined
                            }));
                    }

                    session.updateProgress(lastByte);
                },
                fragmentCancellationToken,
                timeout
            ).then((result: GraphUploadSessionStatus | GraphDriveItem) => {
                session.updateProgress(endByte);

                if (this.isDriveItem(result)) {
                    state = this.getState(state,
                        {
                            item: result
                        });
                } else {
                    state = this.getState(state,
                        {
                            nextExpectedRanges: result.nextExpectedRanges,
                            // If the upload had extra time, then it should be safe to increase the fragment size.
                            speed: Math.min(
                                bandwidthVerifier.didActivityHaveExtraCapacity()
                                    ? speed * this.uploadConfig.speedUpgradeRatio
                                    : speed,
                                1)
                        });
                }

                resolve(state);
            },
                error => {
                    resolve(this.getState(state,
                        {
                            error
                        }));
                });
        });
    }

    private cancelSessionState(session: UploadSessionBase, state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        return this.oneDriveService.cancelUploadSession(state.sessionUrl, session.sessionId, cancellationToken).then(() =>
            this.getState(state, {
                sessionUrl: undefined
            }), () =>
            this.getState(state, {
                sessionUrl: undefined
            }));
    }

    /**
    * Handles a fragment upload or commit error, either by resetting some state or failing out.
    */
    private handleUploadItemErrorState(state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        const {
            speed = 1
        } = state;

        // All fragment upload errors should reset the known progress.
        state = this.getState(state, {
            nextExpectedRanges: undefined
        });

        return this.handleErrorState(state, (code: string | number | HttpStatusCode) => {
            switch (code) {
                case HttpStatusCode.Offline:
                case HttpStatusCode.Unknown:
                    // The message was likely too large to get through. Dramatically reduce the size.
                    return this.windowService.nativeWindow.navigator.onLine ? Promise.resolve(this.getState(state, {
                        speed: Math.max(speed * this.uploadConfig.speedDowngradeRatio, this.uploadConfig.minSpeed)
                    })) : undefined;
                case 'uploadSessionFailed':
                case 'uploadSessionNotFound':
                case HttpStatusCode.NotFound:
                    // Upload session is somehow gone. Need to start over.
                    return Promise.resolve(this.getState(state, {
                        error: undefined,
                        sessionUrl: undefined
                    }));
                //case 'entityTagDoesNotMatch':
                //case 'itemAlreadyExists':
                //case 409:
                //case 412:
                // TODO: Should we allow the user to customize conflict behavior?
                case 'invalidRange':
                case HttpStatusCode.RangeNotSatisfiable:
                    // Fragment overlap. Need to wait for a previous fragment upload to resolve.
                    return this.wait(this.uploadConfig.standardWaitDurationInMs, cancellationToken).then(() =>
                        this.getState(state, {
                            error: undefined
                        }));
            }
            return undefined;
        });
    }

    /**
    * Handles a general API error, either by waiting and resetting some state or failing out.
    */
    private handleGeneralErrorState(state: ResumableUploadState, cancellationToken: PromiseCancellationToken): Promise<ResumableUploadState> {
        const {
            waitDuration = this.uploadConfig.initialWaitDurationInMs,
            clientAttemptsRemaining = this.uploadConfig.numClientAttempts,
            serverAttemptsRemaining = this.uploadConfig.numServerAttempts
        } = state;

        return this.handleErrorState(state, (code: string | number | HttpStatusCode) => {
            switch (code) {
                case 'unauthenticated':
                case HttpStatusCode.Unauthorized:
                case 'lockMismatch':
                case HttpStatusCode.Locked:
                case HttpStatusCode.NotImplemented:
                case 'quotaLimitReached':
                case HttpStatusCode.InsufficientStorage:
                case HttpStatusCode.Offline:
                    return undefined;
            }

            if (typeof code === 'number') {
                if (code >= 400 && code < 500 /* Client error */) {
                    // Retry is not allowed.
                    return undefined;
                }

                if (code === 0 ||
                    code >= 500 && code < 600 /* Transient server error */) {
                    if (!serverAttemptsRemaining) {
                        return undefined;
                    }
                    // Server error. Wait and retry indefinitely via exponential back-off.
                    return this.wait(waitDuration, cancellationToken, true).then(() =>
                        this.getState(state, {
                            error: undefined,
                            waitDuration: Math.min(waitDuration * 2, this.uploadConfig.maxWaitDurationInMs),
                            serverAttemptsRemaining: Math.max(serverAttemptsRemaining - 1, 0)
                        }));
                }

                if (!clientAttemptsRemaining) {
                    // Out of retry attempts.
                    return undefined;
                }

                // Retryable client error. Retry a finite amount of times.
                return this.wait(this.uploadConfig.standardWaitDurationInMs, cancellationToken).then(() =>
                    this.getState(state, {
                        error: undefined,
                        waitDuration: undefined,
                        clientAttemptsRemaining: Math.max(clientAttemptsRemaining - 1, 0)
                    }));
            }
            return undefined;
        });
    }

    private handleErrorState(state: ResumableUploadState, resolveError: (code: string | number) => Promise<ResumableUploadState>): Promise<ResumableUploadState> {
        const {
            error
        } = state;

        let resolution = GraphErrorResolver.resolveGraphError(error, resolveError);

        if (resolution === undefined) {
            resolution = Promise.resolve(state);
        }

        return resolution;
    }

    /**
    * Gets an incremented state based on a current state and a delta.
    */
    private getState(current: ResumableUploadState, increment: ResumableUploadState): ResumableUploadState {
        return {
            ...current,
            ...increment
        };
    }

    /**
    * Gets the next valid byte range for the upload given the expected ranges from the server.
    */
    private getNextRange(fileSize: number, nextExpectedRanges: Array<string> = []): Range {
        return nextExpectedRanges.reduce((nextRange: Range, rangeInput: string): Range => {
            // rangeInput e.g. '0-', '1024-4095'

            const [startInput, endInput] = rangeInput.split('-', 2);
            // startInput e.g. '0', '1024'
            // endInput e.g. '', '4095'

            const startByte = parseInt(startInput, 10);
            // startByte e.g. 0, 1024

            let range: Range;

            if (!nextRange || startByte < nextRange.start && startByte < fileSize) {
                const endByte = endInput === '' ?
                    fileSize :
                    Math.min(
                        parseInt(endInput, 10) + 1, // endInput is the *index* of the last byte
                        fileSize);
                // endByte e.g. 3556, 4096

                range = {
                    start: startByte,
                    end: endByte
                };
            } else {
                range = nextRange;
            }

            return range;
        }, undefined);
    }

    /**
    * Determines whether the object is a OneDrive item (as opposed to a status response)
    */
    private isDriveItem<T>(itemOrOther: GraphDriveItem | T): itemOrOther is GraphDriveItem {
        return !!(itemOrOther as GraphDriveItem).id;
    }

    /**
    * Determines the fragment size to use given a current speed value.
    */
    private getFragmentSize(speed: number): number {
        const rawFragmentSize = speed * this.uploadConfig.maxFragmentSize;

        let normalizedFragmentSize: number;

        if (rawFragmentSize > this.uploadConfig.discreteChunkSize) {
            // If the fragment size is greater than the discrete chunk size, always use a multiple of
            // the discrete chunk size.
            normalizedFragmentSize = rawFragmentSize - rawFragmentSize % this.uploadConfig.discreteChunkSize;
        } else {
            // Otherwise, use a multiple of the minimum chunk size.
            normalizedFragmentSize = rawFragmentSize - rawFragmentSize % this.uploadConfig.minFragmentSize;
        }

        return Math.min(Math.max(normalizedFragmentSize, this.uploadConfig.minFragmentSize), this.uploadConfig.maxFragmentSize);
    }

    private wait(duration: number, cancellationToken?: PromiseCancellationToken, allowWakeWhenOnline: boolean = false): Promise<void> {

        let timeoutId: ReturnType<typeof setTimeout>;
        const timeoutPromise = new Promise(resolve => {
            timeoutId = setTimeout(() => {
                resolve();
            }, duration);
        });

        let onOnline: () => void;
        const wakePromise = new Promise(resolve => {
            onOnline = () => {
                if (allowWakeWhenOnline) {
                    resolve();
                }
            };
            this.windowService.nativeWindow.addEventListener('online', onOnline);
        });

        const cancelPromise = cancellationToken ? cancellationToken.onCancel(() =>
            Promise.reject(null)) : new Promise(() => {/*Never resolve*/ });

        return new Promise<void>(resolve => {
            Promise.race([timeoutPromise, wakePromise, cancelPromise]).then(() => {
                resolve();
            },
                () => {
                    // Don't resolve on cancel
                }).then(() => {
                    this.windowService.nativeWindow.removeEventListener('online', onOnline);
                    clearTimeout(timeoutId);
                });
        });
    }
}
